import { useMemo, useState } from "react";
import { Apple, ArrowRight, BookOpen, Brain, Check, Dumbbell, Flame, Send, Sprout, Target, Trash2, TrendingDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { ProgressRing } from "@/components/ui/ProgressRing";
import { MealCard } from "@/components/MealCard";
import { ExerciseCard } from "@/components/ExerciseCard";
import { MessageBubble } from "@/components/MessageBubble";
import { useDemoMode } from "@/lib/demo-mode";
import type { DemoScenarioId } from "@/lib/demo-mode";
import { calculatePlanProgress } from "@/lib/progress";
import { addDays, formatDay } from "@/lib/dayHelpers";
import { formatDate, formatNumber } from "@/lib/format";
import type { Id } from "../../convex/_generated/dataModel";
import { DEMO_ANCHOR_MS, DEMO_MAX_OFFSET, DEMO_MIN_OFFSET } from "@/lib/demo-mode";
import { cn } from "@/lib/cn";
import { formatKcalBalance } from "../lib/kcal";
import { LibraryView } from "./Library";
import type {
  CustomFoodView,
  FavoriteView,
  RecipeView,
  SavedMealView,
} from "./library-utils";

function DemoBanner() {
  return (
    <Card className="mt-3 border-accent/20 bg-accent-light/70 p-4" data-testid="demo-banner">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-surface p-2 text-accent"><Target size={18} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text">This is a demo app. Install it to get your own BiteTrack app.</p>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">Meal, exercise, and weight logs are local to this preview and reset after refresh.</p>
        </div>
      </div>
    </Card>
  );
}

export function DemoChat() {
  const { chatItems, appendChat, getSummary, installUrl, anchorDate } = useDemoMode();
  const [input, setInput] = useState("");
  const summary = getSummary(anchorDate);
  const percentage = summary.goalKcal ? Math.min(100, (summary.consumedKcal / summary.goalKcal) * 100) : 0;

  const send = () => {
    const prompt = input.trim();
    if (!prompt) return;
    appendChat(prompt);
    setInput("");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="demo-chat-screen">
      <DemoBanner />
      <div className="sticky top-[var(--app-header-h,4.5rem)] z-10 rounded-2xl border border-accent/20 bg-accent-light/95 p-3 backdrop-blur" data-testid="demo-today-bar">
        <div className="flex items-center gap-3">
          <ProgressRing value={percentage} size={46} stroke={5}><span className="text-[10px] font-bold">{Math.round(percentage)}%</span></ProgressRing>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text">{summary.consumedKcal} / {summary.goalKcal} kcal</p>
            <p className="text-xs text-text-muted">{formatKcalBalance(summary.remainingKcal)}</p>
          </div>
          <span className="text-xs font-semibold text-accent">Demo</span>
        </div>
      </div>
      <div className="flex flex-col gap-3" data-testid="demo-message-list">
        {chatItems.map((item, index) => <MessageBubble key={`${item.messageId}-${index}`} item={item} index={index} />)}
      </div>
      <Card className="flex flex-col gap-3 border-accent/20 bg-surface/95 p-3" data-testid="demo-composer">
        <textarea
          data-testid="demo-message-input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); } }}
          rows={2}
          placeholder="Try a sample prompt..."
          className="w-full resize-none rounded-xl border border-border bg-muted px-4 py-3 text-base text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-text-muted">Replies are fixed in demo mode. {installUrl ? "Install to use live AI." : "Install link is not configured yet."}</p>
          {installUrl && <a href={installUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-white active:scale-[0.98] select-none">Install BiteTrack</a>}
          <Button data-testid="demo-send-btn" size="sm" onClick={send} disabled={!input.trim()}><Send size={16} /> Send</Button>
        </div>
      </Card>
    </div>
  );
}

function DemoProgressCard() {
  const { profile } = useDemoMode();
  const progress = calculatePlanProgress(profile, DEMO_ANCHOR_MS);
  if (progress === null) return null;
  return (
    <Card className="flex flex-col gap-3 border-accent/20 bg-accent-light/40 p-4" data-testid="demo-plan-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">60-day plan · phase {progress.phaseNumber}</p>
          <h3 className="mt-1 text-base font-semibold text-text">{progress.daysRemaining} days left in this phase</h3>
        </div>
        <span className="text-sm font-semibold text-accent">{progress.projectedLossKg} kg target</span>
      </div>
      <ProgressBar value={progress.phaseProgressPercent} color="accent" />
      <div className="flex justify-between text-xs text-text-muted">
        <span>{progress.daysIntoPhase} days completed</span>
        <span>Target {progress.targetWeightKg} kg</span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-text-muted">
        <div className="rounded-xl bg-surface px-3 py-2">BMR <strong className="text-text">{progress.bmrKcal ?? "—"}</strong> kcal</div>
        <div className="rounded-xl bg-surface px-3 py-2">TDEE <strong className="text-text">{progress.tdeeKcal ?? "—"}</strong> kcal</div>
        <div className="rounded-xl bg-surface px-3 py-2">BMI <strong className="text-text">{progress.bmi ?? "—"}</strong></div>
        <div className="rounded-xl bg-surface px-3 py-2">Target timing <strong className="text-text">{progress.estimatedWeeksToTarget === null ? "—" : progress.estimatedWeeksToTarget === 0 ? "On target" : `${progress.estimatedWeeksToTarget} weeks`}</strong></div>
      </div>
      <p className="text-xs text-text-muted">Projected target date: <strong className="text-text">{progress.projectedTargetDate === null ? "—" : formatDate(progress.projectedTargetDate)}</strong></p>
    </Card>
  );
}

type DemoEntryEditorProps = {
  readonly kind: "food" | "exercise";
  readonly id: string;
  readonly date: number;
  readonly onClose: () => void;
};

function DemoEntryEditor({ kind, id, date, onClose }: DemoEntryEditorProps) {
  const { getSummary, updateFood, updateExercise, removeFood, removeExercise } = useDemoMode();
  const summary = getSummary(date);
  const food = summary.foodEntries.find((entry) => entry._id === id);
  const exercise = summary.exerciseEntries.find((entry) => entry._id === id);
  const [name, setName] = useState(food?.name ?? exercise?.type ?? "");
  const [calories, setCalories] = useState(String(food?.finalKcal ?? exercise?.finalBurnedKcal ?? 0));

  const save = () => {
    const parsed = Number(calories);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    if (kind === "food") updateFood(id as Id<"foodEntries">, { name, finalKcal: parsed });
    else updateExercise(id as Id<"exerciseEntries">, { type: name, finalBurnedKcal: parsed });
    onClose();
  };

  const remove = () => {
    if (kind === "food") removeFood(id as Id<"foodEntries">);
    else removeExercise(id as Id<"exerciseEntries">);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[env(safe-area-inset-bottom)]">
        <Card className="mx-auto flex w-full max-w-xl flex-col gap-4 p-5" onClick={(event) => event.stopPropagation()} data-testid="demo-edit-sheet">
          <h3 className="text-lg font-semibold text-text">Edit demo {kind}</h3>
          <Input label={kind === "food" ? "Meal" : "Exercise"} value={name} onChange={(event) => setName(event.target.value)} />
          <Input label="Calories" type="number" min="0" value={calories} onChange={(event) => setCalories(event.target.value)} />
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1" onClick={save}><Check size={16} /> Save</Button>
          </div>
          <Button variant="danger" onClick={remove}><Trash2 size={16} /> Remove from preview</Button>
        </Card>
      </div>
    </>
  );
}

export function DemoToday() {
  const { getSummary, addMeal, addExercise, anchorDate, getWeights, upsertWeight } = useDemoMode();
  const [selectedDate, setSelectedDate] = useState(anchorDate);
  const [entryTarget, setEntryTarget] = useState<"meal" | "exercise" | null>(null);
  const [editing, setEditing] = useState<{ kind: "food" | "exercise"; id: string } | null>(null);
  const [name, setName] = useState("");
  const [calories, setCalories] = useState("");
  const [duration, setDuration] = useState("30");
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState<string | null>(null);
  const summary = getSummary(selectedDate);
  const selectedWeight = getWeights(selectedDate, selectedDate)[0];
  const percentage = summary.goalKcal ? Math.min(100, (summary.consumedKcal / summary.goalKcal) * 100) : 0;

  const openForm = (target: "meal" | "exercise") => { setEntryTarget(target); setName(""); setCalories(""); setDuration("30"); };
  const save = () => {
    const parsedCalories = Number(calories);
    if (!name.trim() || !Number.isFinite(parsedCalories) || parsedCalories < 0) return;
    if (entryTarget === "meal") addMeal({ date: selectedDate, name: name.trim(), kcal: parsedCalories });
    if (entryTarget === "exercise") addExercise({ date: selectedDate, type: name.trim(), durationMin: Number(duration) || 30, calories: parsedCalories });
    setEntryTarget(null);
  };
  const saveWeight = () => {
    const value = Number(weightInput);
    if (!Number.isFinite(value) || value <= 0) { setWeightError("Enter a positive weight in kg."); return; }
    upsertWeight({ date: selectedDate, weightKg: value });
    setWeightInput("");
    setWeightError(null);
  };

  return (
    <div className="flex flex-col gap-5" data-testid="demo-today-screen">
      <DemoBanner />
      <DemoProgressCard />
      <div className="flex items-center justify-between gap-2">
         <Button variant="secondary" size="sm" onClick={() => setSelectedDate(Math.max(addDays(anchorDate, DEMO_MIN_OFFSET), addDays(selectedDate, -1)))} disabled={selectedDate <= addDays(anchorDate, DEMO_MIN_OFFSET)}>Previous</Button>
         <span className="text-sm font-semibold text-text">{formatDay(selectedDate)}</span>
         <Button variant="secondary" size="sm" onClick={() => setSelectedDate(Math.min(addDays(anchorDate, DEMO_MAX_OFFSET), addDays(selectedDate, 1)))} disabled={selectedDate >= addDays(anchorDate, DEMO_MAX_OFFSET)}>Next</Button>
      </div>
       <Card className="flex flex-col items-center gap-3 p-5">
        <ProgressRing value={percentage} size={156} stroke={10} color={percentage <= 100 ? "#2f855a" : "#dc2626"}>
          <div className="text-center"><p className="text-3xl font-bold text-text">{formatNumber(summary.consumedKcal)}</p><p className="text-xs text-text-muted">of {summary.goalKcal} kcal</p></div>
        </ProgressRing>
        <div className="flex w-full gap-2"><Button className="flex-1" data-testid="demo-log-meal-btn" onClick={() => openForm("meal")}><Apple size={16} /> Log meal</Button><Button className="flex-1" variant="secondary" data-testid="demo-log-exercise-btn" onClick={() => openForm("exercise")}><Dumbbell size={16} /> Exercise</Button></div>
       </Card>
       <Card className="flex flex-col gap-3 p-4" data-testid="demo-weight-card">
         <div className="flex items-center justify-between gap-3"><div><h2 className="text-sm font-semibold text-text">Body weight</h2><p className="text-xs text-text-muted">Selected day measurement</p></div><span className="text-lg font-bold text-accent">{selectedWeight ? `${selectedWeight.weightKg} kg` : "—"}</span></div>
         <div className="flex gap-2"><Input label="Weight (kg)" type="number" inputMode="decimal" min="1" step="0.1" value={weightInput} onChange={(event) => { setWeightInput(event.target.value); setWeightError(null); }} placeholder="82.0" /><Button className="mt-6 shrink-0" size="sm" onClick={saveWeight}>Save</Button></div>
         {weightError && <p className="text-sm text-danger" role="alert">{weightError}</p>}
       </Card>
       <div className="grid grid-cols-2 gap-3 text-center"><Card className="p-3"><p className="text-lg font-bold text-text">{summary.consumedKcal}</p><p className="text-xs text-text-muted">Consumed</p></Card><Card className="p-3"><p className="text-lg font-bold text-text">{formatKcalBalance(summary.remainingKcal)}</p><p className="text-xs text-text-muted">Balance</p></Card></div>
      {summary.foodEntries.length > 0 && <section className="flex flex-col gap-2"><h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Meals</h2>{summary.foodEntries.map((entry) => <MealCard key={entry._id} entry={entry} onEdit={(item) => setEditing({ kind: "food", id: item._id })} />)}</section>}
      {summary.exerciseEntries.length > 0 && <section className="flex flex-col gap-2"><h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Exercise</h2>{summary.exerciseEntries.map((entry) => <ExerciseCard key={entry._id} entry={entry} onEdit={(item) => setEditing({ kind: "exercise", id: item._id })} />)}</section>}
      {entryTarget && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setEntryTarget(null)} aria-hidden="true" />
          <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[env(safe-area-inset-bottom)]">
            <Card className="mx-auto flex w-full max-w-xl flex-col gap-4 p-5" onClick={(event) => event.stopPropagation()} data-testid="demo-entry-sheet">
              <h3 className="text-lg font-semibold text-text">Log demo {entryTarget}</h3>
              <Input label={entryTarget === "meal" ? "Meal name" : "Exercise type"} value={name} onChange={(event) => setName(event.target.value)} placeholder={entryTarget === "meal" ? "e.g. Salmon salad" : "e.g. Cycling"} />
              <Input label="Calories" type="number" min="0" value={calories} onChange={(event) => setCalories(event.target.value)} placeholder="350" />
              {entryTarget === "exercise" && <Input label="Duration (min)" type="number" min="1" value={duration} onChange={(event) => setDuration(event.target.value)} />}
              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1" onClick={() => setEntryTarget(null)}>Cancel</Button>
                <Button className="flex-1" onClick={save}>Save preview</Button>
              </div>
            </Card>
          </div>
        </>
      )}
       {editing && <DemoEntryEditor kind={editing.kind} id={editing.id} date={selectedDate} onClose={() => setEditing(null)} />}
    </div>
  );
}

export function DemoHistoryCalendar() {
  const { getSummary, getRange, getWeights, anchorDate, profile } = useDemoMode();
  const [selectedDate, setSelectedDate] = useState(anchorDate);
  const days = useMemo(
    () => Array.from(
      { length: DEMO_MAX_OFFSET - DEMO_MIN_OFFSET + 1 },
      (_, index) => addDays(anchorDate, index + DEMO_MIN_OFFSET),
    ),
    [anchorDate],
  );
  const summary = getSummary(selectedDate);
  const range = getRange(addDays(anchorDate, DEMO_MIN_OFFSET), addDays(anchorDate, DEMO_MAX_OFFSET));
  const rangeByDate = new Map(range.map((row) => [row.date, row]));
  const weights = getWeights(addDays(anchorDate, DEMO_MIN_OFFSET), addDays(anchorDate, DEMO_MAX_OFFSET));
  const selectedWeight = weights.find((weight) => weight.date === selectedDate);

  return (
    <div className="flex flex-col gap-5" data-testid="demo-analytics-screen">
      <DemoBanner />
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Progress</p>
        <h2 className="mt-1 text-2xl font-semibold text-text">Daily nutrition progress</h2>
        <p className="mt-1 text-sm text-text-muted">Explore 42 days of sample nutrition and weight data.</p>
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-text">{formatDay(selectedDate)}</span>
          <span className="text-sm font-semibold text-accent">{summary.consumedKcal} kcal</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-text-muted">
          <div className="rounded-xl bg-muted px-3 py-2">Meals <strong className="text-text">{summary.foodEntries.length}</strong></div>
          <div className="rounded-xl bg-muted px-3 py-2">Exercise <strong className="text-text">{summary.exerciseEntries.length}</strong></div>
          <div className="rounded-xl bg-muted px-3 py-2">Weight <strong className="text-text">{selectedWeight?.weightKg ?? "—"} kg</strong></div>
        </div>
      </Card>
      <section aria-label="42-day nutrition calendar" className="flex flex-col gap-2">
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          {(["M", "T", "W", "T", "F", "S", "S"] as const).map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="demo-history-calendar">
          {days.map((date) => {
            const row = rangeByDate.get(date);
            const hasData = row !== undefined && row.entryCount > 0;
            const overGoal = hasData && row.consumedKcal > (profile.dailyGoalKcal ?? 0);
            const weight = weights.find((item) => item.date === date);
            return (
              <button
                key={date}
                type="button"
                onClick={() => setSelectedDate(date)}
                aria-label={`${formatDay(date)}${weight ? `, ${weight.weightKg} kilograms` : ""}`}
                className={cn(
                  "flex min-h-[58px] flex-col items-center justify-center rounded-xl border px-1 py-1 text-center active:scale-[0.98] select-none",
                  date === selectedDate ? "border-accent bg-accent-light text-accent" : "border-border",
                  !hasData ? "bg-muted text-text-muted" : overGoal ? "bg-red-50 text-red-900" : "bg-green-50 text-green-900",
                )}
              >
                <span className="text-xs font-semibold">{new Date(date).getDate()}</span>
                <span className="mt-0.5 text-[10px] leading-tight">{hasData ? row.consumedKcal : "—"}</span>
                {weight && <span className="text-[10px] leading-tight">{weight.weightKg}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-text-muted">
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" />On track</span>
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />Over goal</span>
          <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-muted-foreground" />No meals</span>
        </div>
      </section>
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-text">Selected day details</h3>
        <div className="mt-3 flex flex-col gap-2">
          {summary.foodEntries.length === 0 && summary.exerciseEntries.length === 0 ? (
            <p className="text-sm text-text-muted">No nutrition entries for this day.</p>
          ) : (
            <>
              <p className="text-sm text-text-muted">{summary.consumedKcal} kcal consumed · {formatKcalBalance(summary.remainingKcal)}</p>
              {summary.foodEntries.map((entry) => <p key={entry._id} className="text-sm text-text">{entry.name} · {entry.finalKcal ?? entry.estimatedKcal} kcal</p>)}
            </>
          )}
          {selectedWeight && <p className="text-sm text-text">Weight: {selectedWeight.weightKg} kg</p>}
        </div>
      </Card>
    </div>
  );
}

export function DemoAnalytics() {
  const { getSummary, getRange, getWeights, anchorDate, profile } = useDemoMode();
  const [selectedDate, setSelectedDate] = useState(anchorDate);
  const days = useMemo(() => Array.from({ length: DEMO_MAX_OFFSET - DEMO_MIN_OFFSET + 1 }, (_, index) => addDays(anchorDate, index + DEMO_MIN_OFFSET)), [anchorDate]);
  const summary = getSummary(selectedDate);
  const range = getRange(addDays(anchorDate, DEMO_MIN_OFFSET), addDays(anchorDate, DEMO_MAX_OFFSET));
  const rangeByDate = new Map(range.map((row) => [row.date, row]));
  const weights = getWeights(addDays(anchorDate, DEMO_MIN_OFFSET), addDays(anchorDate, DEMO_MAX_OFFSET));
  return <div className="flex flex-col gap-5" data-testid="demo-analytics-screen"><DemoBanner /><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Progress</p><h2 className="mt-1 text-2xl font-semibold text-text">Daily nutrition progress</h2><p className="mt-1 text-sm text-text-muted">Green days meet your {profile.dailyGoalKcal} kcal goal. Red days are over goal.</p></div><Card className="p-4"><div className="flex items-center justify-between"><span className="text-sm font-semibold text-text">{formatDay(selectedDate)}</span><span className="text-sm font-semibold text-accent">{summary.consumedKcal} kcal</span></div><div className="mt-3 grid grid-cols-3 gap-2 text-xs text-text-muted"><div className="rounded-xl bg-muted px-3 py-2">Meals <strong className="text-text">{summary.foodEntries.length}</strong></div><div className="rounded-xl bg-muted px-3 py-2">Exercise <strong className="text-text">{summary.exerciseEntries.length}</strong></div><div className="rounded-xl bg-muted px-3 py-2">Weight <strong className="text-text">{weights.find((weight) => weight.date === selectedDate)?.weightKg ?? "—"} kg</strong></div></div></Card><div className="flex flex-col gap-2">{days.map((date) => { const row = rangeByDate.get(date); const hasData = row !== undefined && row.entryCount > 0; const overGoal = hasData && row.consumedKcal > (profile.dailyGoalKcal ?? 0); return <button key={date} type="button" onClick={() => setSelectedDate(date)} className={cn("flex min-h-[56px] items-center justify-between rounded-xl border px-4 py-3 text-left active:scale-[0.98] select-none", date === selectedDate ? "border-accent" : "border-border", !hasData ? "bg-muted text-text-muted" : overGoal ? "bg-red-50 text-red-900" : "bg-green-50 text-green-900")}><span className="text-sm font-medium">{formatDay(date)}</span><span className="text-right text-sm font-semibold">{hasData ? `${row.consumedKcal} kcal` : "No data"}<br /><span className="text-xs font-normal">{weights.find((weight) => weight.date === date)?.weightKg ? `${weights.find((weight) => weight.date === date)?.weightKg} kg` : ""}</span></span></button>; })}</div><Card className="p-4"><h3 className="text-sm font-semibold text-text">Selected day details</h3><div className="mt-3 flex flex-col gap-2">{summary.foodEntries.length === 0 && summary.exerciseEntries.length === 0 ? <p className="text-sm text-text-muted">No nutrition entries for this day.</p> : <><p className="text-sm text-text-muted">{summary.consumedKcal} kcal consumed · {formatKcalBalance(summary.remainingKcal)}</p>{summary.foodEntries.map((entry) => <p key={entry._id} className="text-sm text-text">{entry.name} · {entry.finalKcal ?? entry.estimatedKcal} kcal</p>)}</>}{weights.find((weight) => weight.date === selectedDate) && <p className="text-sm text-text">Weight: {weights.find((weight) => weight.date === selectedDate)?.weightKg} kg</p>}</div></Card></div>;
}

const DEMO_LIBRARY_SAVED: SavedMealView[] = [
  {
    _id: "demo-saved-1",
    name: "Usual lunch",
    mealType: "lunch",
    itemsJson: JSON.stringify([
      { name: "Chicken salad bowl", quantity: 1, unit: "bowl", finalKcal: 450, dataSource: "ai-estimate" },
      { name: "Iced green tea", quantity: 1, unit: "bottle", finalKcal: 90, dataSource: "user-entered" },
    ]),
    totalCalories: 540,
    favorite: true,
    timesUsed: 5,
    lastUsedAt: DEMO_ANCHOR_MS - 86_400_000,
  },
];

const DEMO_LIBRARY_CUSTOM: CustomFoodView[] = [
  { _id: "demo-custom-1", name: "Homemade protein bar", servingUnit: "1 bar", calories: 230, proteinG: 18, carbsG: 21, fatG: 8, source: "manual", favorite: false, timesUsed: 3 },
];

const DEMO_LIBRARY_RECIPES: RecipeView[] = [
  {
    _id: "demo-recipe-1",
    name: "Sunday chilli",
    ingredients: [
      { name: "Beef mince", calories: 800, proteinG: 60, carbsG: 0, fatG: 50 },
      { name: "Chopped tomatoes", calories: 90, carbsG: 18 },
    ],
    servings: 4,
    favorite: false,
    timesUsed: 2,
    totals: { calories: 890, proteinG: 60, carbsG: 18, complete: false },
  },
];

export function DemoLibrary() {
  const { memories, library } = useDemoMode();
  const [search, setSearch] = useState("");
  const [favOverrides, setFavOverrides] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const firstMemory = memories[0];
  const favorites: FavoriteView[] = [
    { kind: "savedMeal", refId: "demo-saved-1", name: "Usual lunch", calories: 540, servingLabel: "lunch" },
    ...(firstMemory === undefined
      ? []
      : [{ kind: "foodMemory", refId: firstMemory._id, name: firstMemory.foodName, calories: firstMemory.calories, servingLabel: firstMemory.servingSize } as FavoriteView]),
  ];

  const applyFavoriteOverrides = <T extends { readonly _id: string; readonly favorite?: boolean }>(
    kind: string,
    rows: readonly T[],
  ): T[] =>
    rows.map((row) => {
      const override = favOverrides[`${kind}:${row._id}`];
      return override === undefined ? row : { ...row, favorite: override };
    });

  const demoNotice = (message: string) => setNotice(`Demo preview — ${message}`);

  return (
    <div className="flex flex-col gap-5" data-testid="demo-library-screen">
      <DemoBanner />
      {notice !== null && (
        <p data-testid="demo-library-notice" className="rounded-xl border border-accent/20 bg-accent-light px-4 py-3 text-sm text-accent">
          {notice}
        </p>
      )}
      <LibraryView
        search={search}
        onSearchChange={setSearch}
        recentFoods={library.recent}
        frequentFoods={library.frequent}
        savedMeals={applyFavoriteOverrides("savedMeal", DEMO_LIBRARY_SAVED)}
        memories={applyFavoriteOverrides("foodMemory", memories)}
        favorites={favorites}
        customFoods={applyFavoriteOverrides("customFood", DEMO_LIBRARY_CUSTOM)}
        recipes={applyFavoriteOverrides("recipe", DEMO_LIBRARY_RECIPES)}
        yesterdayEntries={library.yesterday}
        onStartReview={(items) => demoNotice(`the meal review would open with ${items.length} item${items.length === 1 ? "" : "s"}. Install BiteTrack to log foods.`)}
        onAddToPlate={(item) => demoNotice(`"${item.name}" would join your plate.`)}
        onToggleFavorite={(target) =>
          setFavOverrides((current) => ({
            ...current,
            [`${target.kind}:${target.id}`]: !target.favorite,
          }))
        }
        onSaveMemory={() => {
          demoNotice("memories are read-only here. Install BiteTrack to save your own.");
          return true;
        }}
        onDeleteMemory={() => {
          demoNotice("memories are read-only here.");
          return true;
        }}
        onEditEntity={() => {
          demoNotice("library items are read-only here.");
          return true;
        }}
        onDuplicate={() => demoNotice("library items are read-only here.")}
        onDelete={() => {
          demoNotice("library items are read-only here.");
          return true;
        }}
      />
    </div>
  );
}

const SCENARIO_ICONS: Record<DemoScenarioId, LucideIcon> = {
  progress: TrendingDown,
  fresh: Sprout,
  comeback: Flame,
};

export function DemoSettings({ onSwitchScreen }: { onSwitchScreen?: (screen: string) => void }) {
  const { disableDemo, installUrl, scenario, scenarios, setScenario } = useDemoMode();
  return (
    <div className="flex flex-col gap-5" data-testid="demo-settings-screen">
      <DemoBanner />
      <section className="flex flex-col gap-3" data-testid="demo-scenario-picker">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">Demo scenarios</p>
          <h2 className="mt-1 text-lg font-semibold text-text">Pick the story you want to record</h2>
          <p className="mt-1 text-sm leading-relaxed text-text-muted">Every scenario re-seeds Chat, Today, Plan, Progress, and Library instantly with its own consistent data.</p>
        </div>
        {scenarios.map((item) => {
          const Icon = SCENARIO_ICONS[item.id];
          const active = item.id === scenario;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`demo-scenario-${item.id}`}
              aria-pressed={active}
              onClick={() => setScenario(item.id)}
              className={cn(
                "flex flex-col gap-2 rounded-2xl border p-4 text-left transition-all duration-200 ease-out active:scale-[0.98] select-none",
                active ? "border-accent bg-accent-light/70 shadow-sm" : "border-border bg-surface",
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn("rounded-full p-2", active ? "bg-accent text-white" : "bg-muted text-text-muted")}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-text">{item.title}</p>
                  <p className="text-xs leading-relaxed text-text-muted">{item.story}</p>
                </div>
                {active && (
                  <span className="flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-[10px] font-semibold text-white" data-testid="demo-scenario-active">
                    <Check size={12} /> Active
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {item.statChips.map((chip) => (
                  <span key={chip} className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", active ? "bg-surface text-accent" : "bg-muted text-text-muted")}>
                    {chip}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </section>
      <Card className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <BookOpen size={20} className="text-text-muted" />
          <h3 className="text-base font-semibold text-text">Guide</h3>
        </div>
        <p className="text-sm leading-relaxed text-text-muted">How BiteTrack works — the daily loop, the five screens, and everything the chat agent can do.</p>
        <Button data-testid="demo-open-guide-btn" variant="secondary" disabled={onSwitchScreen === undefined} onClick={() => onSwitchScreen?.("guide")}>Open the guide</Button>
      </Card>
      <Card className="flex flex-col gap-4 p-5">
        <p className="text-sm leading-relaxed text-text-muted">Install BiteTrack to create your own profile, connect services, and keep real logs.</p>
        {installUrl ? <Button data-testid="demo-install-settings-btn" onClick={() => window.open(installUrl, "_blank", "noopener,noreferrer")}><ArrowRight size={16} /> Install the app</Button> : <p className="rounded-xl border border-border bg-muted p-3 text-sm text-text-muted">Install link is not configured yet.</p>}
        <Button data-testid="demo-disable-btn" variant="secondary" onClick={disableDemo}>Exit demo mode</Button>
      </Card>
    </div>
  );
}
